import { useState, useEffect, useMemo } from 'react';
import { useProtectedAction } from '../hooks/useProtectedAction';
import { useAuth } from '../context/AuthContext';
import { useLeads } from '../context/LeadsContext';
import { db } from '../services/firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';

const N8N = 'https://fatima.agenciafalei.com.br/webhook';

// Volume por dia — quem decide é o franqueado (chip frio x chip maduro).
// O ritmo dentro do dia (55–240s, blocos de 3–8, pausas de 5–10min) fica no motor e não muda.
const POR_DIA_PADRAO = 10;
const POR_DIA_ALERTA = 20;      // acima disso, pergunta a maturidade do número
const HORA_FIM_PADRAO = '20:00';
const MATURIDADE = {
  novo:   { label: 'Menos de 1 mês',                 teto: 10,   msg: 'Melhor ficar em até 10 por dia. Números novos que disparam muito costumam ser bloqueados na primeira semana.' },
  medio:  { label: '1 a 3 meses',                    teto: 20,   msg: 'Até 20 por dia é uma faixa segura para esse tempo de uso.' },
  maduro: { label: 'Mais de 3 meses, uso diário',    teto: null, msg: 'Pode seguir. Se notar demora na entrega ou mensagens não chegando, reduza.' },
};

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const hojeISO = () => new Date().toLocaleDateString('en-CA');
const horaAgora = () => new Date().toTimeString().slice(0, 5);
const fmtData = iso => iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '';
const fmtDataHora = iso => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

const STATUS_UI = {
  aguardando: { txt: 'Aguardando horário', cor: '#d97706', bg: 'rgba(245,158,11,0.10)' },
  ativo:      { txt: 'Enviando',           cor: '#059669', bg: 'rgba(16,185,129,0.08)' },
  pausado:    { txt: 'Pausado',            cor: '#6b7280', bg: 'rgba(156,163,175,0.12)' },
  concluido:  { txt: 'Concluído',          cor: '#6c47ff', bg: 'rgba(108,71,255,0.08)' },
  cancelado:  { txt: 'Cancelado',          cor: '#dc2626', bg: 'rgba(239,68,68,0.08)' },
};

function montarContatos(lista) {
  const t = lista.split('\n').filter(l => l.trim()).map(l => {
    const [tel, nome] = l.split(',');
    return { telefone: tel.trim().replace(/\D/g, ''), nome: (nome || '').trim() || 'Amigo' };
  }).filter(c => c.telefone.length >= 10);
  for (let s = t.length - 1; s > 0; s--) { const r = Math.floor(Math.random() * (s + 1)); [t[s], t[r]] = [t[r], t[s]]; }
  return t;
}

// Refatia uma lista de contatos em blocos de N por dia.
const fatiar = (lista, porDia) => {
  const out = [];
  for (let i = 0; i < lista.length; i += porDia) out.push(lista.slice(i, i + porDia));
  return out;
};

// dias de envio a partir de uma data, pulando fim de semana se necessário
function planejarDias(inicioISO, blocos, fimDeSemana) {
  const dias = [];
  const d = new Date(inicioISO + 'T12:00:00');
  while (dias.length < blocos) {
    const dow = d.getDay();
    if (fimDeSemana || (dow >= 1 && dow <= 5)) dias.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return dias;
}

export default function Disparo() {
  const [missao, setMissao]         = useState('');
  const [segmento, setSegmento]     = useState('');
  const [instancia, setInstancia]   = useState('');
  const [aparelhos, setAparelhos]   = useState([]);
  const [porAparelho, setPorAparelho] = useState({});   // { nome: { maturidade, ultimoPorDia } }
  const [lista, setLista]           = useState('');
  const [porDia, setPorDia]         = useState(POR_DIA_PADRAO);
  const [maturidade, setMaturidade] = useState('');     // resposta para o aparelho atual
  const [quando, setQuando]         = useState('agora'); // 'agora' | 'agendar'
  const [data, setData]             = useState(hojeISO());
  const [hora, setHora]             = useState('09:00');
  const [horaFim, setHoraFim]       = useState(HORA_FIM_PADRAO);
  const [fimDeSemana, setFimDeSemana] = useState(false);
  const [titulo, setTitulo]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [status, setStatus]         = useState('');
  const [jobs, setJobs]             = useState([]);
  const [uid, setUid]               = useState(null);
  const [editJob, setEditJob]       = useState(null);   // disparo aberto para edição

  const { protect, Modals, logado, temPlano } = useProtectedAction(true);
  const { paraDisparo, setParaDisparo } = useLeads();
  // Os disparos são da conta, não de quem está logado: um vendedor
  // convidado dispara pelos aparelhos do franqueado e os agendamentos
  // dele aparecem para o time inteiro.
  const { contaUid } = useAuth();

  const bloqueado = !logado || !temPlano;
  const contatos  = lista.split('\n').filter(l => l.trim()).length;
  const porDiaNum = Math.max(1, parseInt(porDia) || 1);
  const blocos    = contatos ? Math.ceil(contatos / porDiaNum) : 0;

  // ── aparelhos + memória por aparelho ──────────────────────────────
  useEffect(() => {
    if (!contaUid) { setAparelhos([]); setUid(null); return; }
    setUid(contaUid);
    let vivo = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', contaUid, 'config', 'aparelhos'));
        if (!vivo) return;
        const d = snap.exists() ? snap.data() : {};
        const l = d.lista || [];
        setAparelhos(l);
        setPorAparelho(d.porAparelho || {});
        setInstancia(prev => prev || l[0] || '');
      } catch (e) { if (vivo) setAparelhos([]); }
    })();
    return () => { vivo = false; };
  }, [contaUid]);

  // ao trocar de aparelho, puxa o que ele usou da última vez
  useEffect(() => {
    if (!instancia) return;
    const m = porAparelho[instancia] || {};
    setMaturidade(m.maturidade || '');
    setPorDia(m.ultimoPorDia || POR_DIA_PADRAO);
    // eslint-disable-next-line
  }, [instancia, porAparelho]);

  // ── lista de disparos (em andamento + histórico), ao vivo ─────────
  useEffect(() => {
    if (!uid) { setJobs([]); return; }
    const q = query(collection(db, 'users', uid, 'agendamentos'), orderBy('criadoEm', 'desc'), limit(50));
    const unsub = onSnapshot(q, snap => setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => setJobs([]));
    return () => unsub();
  }, [uid]);

  // ── leads vindos de Meus Leads / Clientes ─────────────────────────
  useEffect(() => {
    if (paraDisparo && paraDisparo.length) {
      const linhas = paraDisparo.map(l => {
        const tel = l.telefone_fmt || String(l.telefone || '').replace(/\D/g, '');
        return `${tel}, ${l.nome || ''}`.trim();
      }).filter(l => l.replace(/\D/g, '').length >= 10).join('\n');
      setLista(linhas);
      setStatus(`✅ ${paraDisparo.length} contatos carregados. Escreva a missão e dispare.`);
      setParaDisparo(null);
    }
    // eslint-disable-next-line
  }, [paraDisparo]);

  // ── maturidade do número ──────────────────────────────────────────
  const teto = maturidade ? MATURIDADE[maturidade].teto : null;
  const acimaDoTeto = teto !== null && porDiaNum > teto;
  const perguntarMaturidade = porDiaNum > POR_DIA_ALERTA && (!maturidade || acimaDoTeto);

  const responderMaturidade = (k) => {
    setMaturidade(k);
    const t = MATURIDADE[k].teto;
    if (t !== null && porDiaNum > t) setPorDia(t);
  };

  // ── preview do plano ──────────────────────────────────────────────
  const preview = useMemo(() => {
    if (!contatos) return '';
    const inicio = quando === 'agora' ? hojeISO() : data;
    const dias = planejarDias(inicio, blocos, fimDeSemana);
    const f = d => `${DIAS[d.getDay()].toLowerCase()} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (blocos === 1) return `${contatos} contatos → 1 dia de envio (${quando === 'agora' ? 'hoje' : f(dias[0])}).`;
    return `${contatos} contatos → ${blocos} dias de envio, até ${porDiaNum} por dia: de ${quando === 'agora' ? 'hoje' : f(dias[0])} a ${f(dias[dias.length - 1])}${fimDeSemana ? '' : ' (pulando fim de semana)'}.`;
  }, [contatos, blocos, porDiaNum, quando, data, fimDeSemana]);

  // ── disparar (tudo pelo motor) ────────────────────────────────────
  const disparar = protect(async () => {
    if (!missao.trim())    { setStatus('⚠ Informe a missão.'); return; }
    if (!aparelhos.length) { setStatus('⚠ Ainda não há números conectados. Conecte um em Configurações antes de disparar.'); return; }
    if (!instancia)        { setStatus('⚠ Selecione o aparelho de envio.'); return; }
    if (!lista.trim())     { setStatus('⚠ Adicione os contatos.'); return; }
    if (!uid)              { setStatus('⚠ Entre novamente para disparar.'); return; }
    if (perguntarMaturidade) { setStatus('⚠ Responda quanto tempo de uso tem esse número antes de disparar.'); return; }
    if (quando === 'agendar' && horaFim <= hora) { setStatus('⚠ O horário final precisa ser depois do inicial.'); return; }

    const targets = montarContatos(lista);
    if (!targets.length) { setStatus('⚠ Nenhum número válido na lista (precisa de DDD + número, ex.: 5541999887766).'); return; }

    const agora = quando === 'agora';
    setLoading(true);
    setStatus('⟳ Criando disparo...');
    try {
      const r = await fetch(`${N8N}/criar-agendamento`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid,
          titulo: titulo.trim() || (missao.trim().slice(0, 40) + (missao.trim().length > 40 ? '…' : '')),
          missao, segmento, instancia,
          contatos: targets,
          disparosPorDia: porDiaNum,
          inicioImediato: agora,
          inicio: agora ? hojeISO() : data,
          horario: agora ? horaAgora() : hora,
          horarioFim: agora ? '23:59' : horaFim,
          fimDeSemana,
          maturidade,
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.erro || 'erro no servidor');

      // Agendar é o que "consome" o lead: desconta do saldo do extrator na hora,
      // mesmo que o envio leve dias. É isso que deixa planejar a semana sem travar.
      try {
        const refUso = doc(db, 'users', uid, 'meta', 'uso_extrator');
        const snap = await getDoc(refUso);
        const atual = snap.exists() ? (snap.data().leadsParados || 0) : 0;
        await setDoc(refUso, { leadsParados: Math.max(0, atual - targets.length) }, { merge: true });
      } catch (e) { /* saldo é secundário: não pode impedir o agendamento */ }

      // lembra volume e maturidade por aparelho (merge não apaga limiteAparelhos nem lista)
      try {
        await setDoc(doc(db, 'users', uid, 'config', 'aparelhos'),
          { porAparelho: { [instancia]: { ultimoPorDia: porDiaNum, maturidade: maturidade || '' } } }, { merge: true });
        setPorAparelho(p => ({ ...p, [instancia]: { ultimoPorDia: porDiaNum, maturidade } }));
      } catch (e) { console.warn('porAparelho', e); }

      setStatus(agora
        ? `✅ Disparo iniciado: ${d.totalContatos} contatos em ${d.totalBlocos} dia${d.totalBlocos === 1 ? '' : 's'}. As primeiras mensagens saem em até 1 minuto — pode fechar o sistema.`
        : `✅ Agendado: ${d.totalContatos} contatos em ${d.totalBlocos} dia${d.totalBlocos === 1 ? '' : 's'}, a partir de ${fmtData(d.inicio)} às ${d.horario}.`);
      setLista(''); setTitulo('');
    } catch (e) {
      console.error('criar-agendamento', e);
      setStatus('⚠ Não foi possível criar o disparo: ' + e.message);
    }
    setLoading(false);
  });

  // ── ações nos jobs ────────────────────────────────────────────────
  const mudarStatus = async (job, novo) => {
    try { await updateDoc(doc(db, 'users', uid, 'agendamentos', job.id), { status: novo }); }
    catch (e) { console.error(e); setStatus('⚠ Não foi possível alterar o disparo.'); }
  };
  const retomar = (job) => {
    const noMeio = (job.idxNoBloco || 0) > 0 && job.blocoAtivoJson && job.blocoAtivoJson !== '[]';
    mudarStatus(job, noMeio ? 'ativo' : 'aguardando');
  };
  const cancelar = (job) => {
    if (window.confirm('Cancelar este disparo? Os contatos ainda não enviados não receberão a mensagem.')) mudarStatus(job, 'cancelado');
  };
  const reutilizar = (job, comMissao) => {
    let b = []; try { b = JSON.parse(job.blocosJson || '[]'); } catch (e) {}
    const grupo = b.flat();
    setLista(grupo.map(c => `${c.telefone}, ${c.nome || ''}`.trim()).join('\n'));
    if (comMissao) {
      setMissao(job.missao || ''); setSegmento(job.segmento || '');
      setTitulo((job.titulo || 'Disparo') + ' (nova rodada)');
    } else { setMissao(''); setTitulo(''); }
    if (job.instancia && aparelhos.includes(job.instancia)) setInstancia(job.instancia);
    if (job.disparosPorDia) setPorDia(job.disparosPorDia);
    setFimDeSemana(!!job.fimDeSemana);
    setQuando('agora');
    setStatus(`✅ ${grupo.length} contatos carregados de "${job.titulo}". ${comMissao ? 'Missão também.' : 'Escreva a nova missão.'} Revise e dispare.`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const emAndamento = jobs.filter(j => ['aguardando', 'ativo', 'pausado'].includes(j.status));
  const finalizados = jobs.filter(j => ['concluido', 'cancelado'].includes(j.status)).slice(0, 10);

  return (
    <div style={{ maxWidth: '820px', margin: '0 auto' }}>
      {Modals}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px', fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>Disparos</h1>
        <p style={{ color: '#52526e', fontSize: '13px' }}>Mensagens únicas e humanizadas por IA. O envio roda no servidor: pode fechar o sistema.</p>
      </div>

      <div style={card}>
        <div style={{ marginBottom: '16px' }}>
          <label style={lbl}>Missão / mensagem base</label>
          <textarea value={missao} onChange={e => setMissao(e.target.value)}
            placeholder="Descreva o objetivo da mensagem..."
            style={{ ...inp, minHeight: '90px', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div><label style={lbl}>Segmento <span style={{ opacity: .5 }}>(opcional)</span></label>
            <input value={segmento} onChange={e => setSegmento(e.target.value)} placeholder="Ex: Barbearia" style={inp} /></div>
          <div><label style={lbl}>Aparelho de envio</label>
            {aparelhos.length === 0 ? (
              <div style={{ ...inp, display: 'flex', alignItems: 'center', color: '#9898b8', fontSize: '13px' }}>
                📵 Ainda não há números conectados — conecte em Configurações
              </div>
            ) : (
              <select value={instancia} onChange={e => setInstancia(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {aparelhos.map((nome, i) => <option key={i} value={nome}>{nome}</option>)}
              </select>
            )}</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={lbl}>Contatos {contatos > 0 && <span style={{ color: '#6c47ff', marginLeft: '8px' }}>{contatos} números</span>}</label>
          <textarea value={lista} onChange={e => setLista(e.target.value)}
            placeholder={'Um por linha: 5541999887766, Nome'}
            style={{ ...inp, minHeight: '120px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }} />
        </div>

        {/* volume por dia + maturidade */}
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '16px', alignItems: 'start', marginBottom: '16px' }}>
          <div>
            <label style={lbl}>Quantos por dia</label>
            <input type="number" min="1" max="300" value={porDia} onChange={e => setPorDia(e.target.value)} style={inp} />
          </div>
          <div style={{ fontSize: '12px', color: '#52526e', lineHeight: 1.5, paddingTop: '22px' }}>
            Chip novo: até <b>10</b>/dia. Com algumas semanas de uso: <b>20–30</b>. Acima disso, só número maduro.
            {maturidade && !perguntarMaturidade && <span style={{ color: '#9898b8' }}> · Este número: {MATURIDADE[maturidade].label.toLowerCase()}</span>}
          </div>
        </div>

        {perguntarMaturidade && (
          <div style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px' }}>
              {acimaDoTeto ? `Para ${porDiaNum}/dia, confirme: esse número tem quanto tempo de uso no WhatsApp?` : 'Esse número tem quanto tempo de uso no WhatsApp?'}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(MATURIDADE).map(([k, m]) => (
                <button key={k} type="button" onClick={() => responderMaturidade(k)} style={{
                  padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                  border: '1.5px solid ' + (maturidade === k ? '#d97706' : 'rgba(0,0,0,0.1)'),
                  background: maturidade === k ? '#d97706' : '#fff', color: maturidade === k ? '#fff' : '#52526e' }}>{m.label}</button>
              ))}
            </div>
            {maturidade && <div style={{ fontSize: '12px', color: '#92400e', marginTop: '10px' }}>{MATURIDADE[maturidade].msg}</div>}
          </div>
        )}
        {maturidade && !perguntarMaturidade && porDiaNum > POR_DIA_ALERTA && (
          <div style={{ fontSize: '12px', color: '#92400e', marginTop: '-8px', marginBottom: '16px' }}>{MATURIDADE[maturidade].msg}</div>
        )}

        {/* quando */}
        <div style={{ marginBottom: '16px' }}>
          <label style={lbl}>Começar</label>
          <div style={{ display: 'flex', gap: '6px', background: '#f0f0f7', padding: '4px', borderRadius: '10px', width: 'fit-content', marginBottom: quando === 'agendar' ? '12px' : 0 }}>
            {[['agora', '🚀 Agora'], ['agendar', '📅 Em uma data']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setQuando(v)} style={{
                background: quando === v ? '#fff' : 'transparent', color: quando === v ? '#0a0a18' : '#52526e',
                border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                boxShadow: quando === v ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
            ))}
          </div>
          {quando === 'agendar' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.4fr', gap: '12px' }}>
              <div><label style={lbl}>Data</label><input type="date" value={data} min={hojeISO()} onChange={e => setData(e.target.value)} style={inp} /></div>
              <div><label style={lbl}>Das</label><input type="time" value={hora} onChange={e => setHora(e.target.value)} style={inp} /></div>
              <div><label style={lbl}>Até</label><input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)} style={inp} /></div>
              <div><label style={lbl}>Nome <span style={{ opacity: .5 }}>(opcional)</span></label><input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ex: Reativação agosto" style={inp} /></div>
            </div>
          )}
        </div>

        {(
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#52526e', marginBottom: '16px', cursor: 'pointer', width: 'fit-content' }}>
            <input type="checkbox" checked={fimDeSemana} onChange={e => setFimDeSemana(e.target.checked)} />
            Enviar também aos fins de semana
            <span style={{ fontSize: '11px', color: '#9898b8' }}>(a data que você escolher sempre vale)</span>
          </label>
        )}

        {preview && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', background: 'rgba(108,71,255,0.05)',
            border: '1px solid rgba(108,71,255,0.15)', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
            <span style={{ fontSize: '16px' }}>📡</span>
            <span style={{ fontSize: '12px', color: '#52526e', lineHeight: 1.5 }}>
              <b style={{ color: '#0a0a18' }}>{preview}</b><br />
              Dentro de cada dia as mensagens saem no ritmo natural, com pausas, para proteger seu número.
            </span>
          </div>
        )}

        {bloqueado && (
          <div style={{ background: 'rgba(108,71,255,0.06)', border: '1px solid rgba(108,71,255,0.2)',
            borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#6c47ff' }}>
            {!logado ? '🔒 Entre para usar o disparo' : '🔒 Faça seu plano para disparar mensagens'}
          </div>
        )}

        {status && (
          <div style={{ padding: '10px 14px', borderRadius: '9px', marginBottom: '14px', fontSize: '12px', fontFamily: 'monospace',
            background: status.startsWith('✅') ? 'rgba(16,185,129,0.08)' : status.startsWith('⟳') ? 'rgba(108,71,255,0.08)' : 'rgba(239,68,68,0.08)',
            color: status.startsWith('✅') ? '#059669' : status.startsWith('⟳') ? '#6c47ff' : '#dc2626',
            border: '1px solid ' + (status.startsWith('✅') ? 'rgba(16,185,129,0.2)' : status.startsWith('⟳') ? 'rgba(108,71,255,0.2)' : 'rgba(239,68,68,0.2)') }}>{status}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={disparar} disabled={loading} style={{
            background: loading ? '#9898b8' : 'linear-gradient(135deg, #6c47ff, #5535e0)',
            color: '#fff', border: 'none', borderRadius: '10px',
            padding: '11px 28px', fontSize: '14px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? '⟳ Criando...' : bloqueado ? '🔒 ' + (!logado ? 'Entrar' : 'Ver planos') : quando === 'agora' ? '🚀 Disparar →' : '📅 Agendar →'}
          </button>
        </div>
      </div>

      {uid && jobs.length > 0 && (
        <div style={{ ...card, marginTop: '20px' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '17px', fontWeight: '800', marginBottom: '14px' }}>Em andamento</h2>
          {emAndamento.length === 0 && <p style={{ fontSize: '13px', color: '#9898b8' }}>Nenhum disparo em andamento.</p>}
          {emAndamento.map(j => <JobCard key={j.id} uid={uid} job={j} aparelhos={aparelhos}
            onReutilizar={() => reutilizar(j, true)} onSoGrupo={() => reutilizar(j, false)}
            onEditar={() => setEditJob(j)}
            onPausar={() => mudarStatus(j, 'pausado')} onRetomar={() => retomar(j)} onCancelar={() => cancelar(j)} />)}
          {finalizados.length > 0 && (
            <>
              <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: '17px', fontWeight: '800', margin: '22px 0 14px' }}>Histórico</h2>
              {finalizados.map(j => <JobCard key={j.id} uid={uid} job={j} aparelhos={aparelhos} onReutilizar={() => reutilizar(j, true)} onSoGrupo={() => reutilizar(j, false)} />)}
            </>
          )}
        </div>
      )}

      {editJob && (
        <EditarJobModal
          uid={uid}
          job={jobs.find(j => j.id === editJob.id) || editJob}
          aparelhos={aparelhos}
          onClose={() => setEditJob(null)}
          onSalvo={(msg) => { setEditJob(null); setStatus(msg); }}
        />
      )}
    </div>
  );
}

// ── Editar um disparo já criado ───────────────────────────────────────────────
// Antes, se a instância caísse, a lista agendada morria junto: não dava pra
// trocar o aparelho nem adiar. Aqui edita direto no documento do agendamento.
// Blocos já enviados nunca são tocados — só o que ainda não saiu é refatiado.
function EditarJobModal({ uid, job, aparelhos, onClose, onSalvo }) {
  const [f, setF] = useState(() => ({
    titulo:     job.titulo || '',
    missao:     job.missao || '',
    segmento:   job.segmento || '',
    instancia:  job.instancia || '',
    inicio:     String(job.inicio || hojeISO()).slice(0, 10),
    horario:    job.horario || '09:00',
    horarioFim: job.horarioFim || HORA_FIM_PADRAO,
    porDia:     job.disparosPorDia || POR_DIA_PADRAO,
    fimDeSemana: !!job.fimDeSemana,
  }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const blocos = useMemo(() => {
    try { return JSON.parse(job.blocosJson || '[]'); } catch (e) { return []; }
  }, [job.blocosJson]);

  // O bloco que já começou a sair fica congelado: refatiar ele reenviaria
  // mensagem pra quem já recebeu.
  const blocoAtual   = job.blocoAtual || 0;
  const noMeio       = (job.idxNoBloco || 0) > 0;
  const primeiroLivre = Math.min(blocos.length, noMeio ? blocoAtual + 1 : blocoAtual);
  const travados     = blocos.slice(0, primeiroLivre);
  const restantes    = blocos.slice(primeiroLivre).flat();

  const porDiaNum = Math.max(1, parseInt(f.porDia) || 1);
  const mudouPorDia = porDiaNum !== (job.disparosPorDia || porDiaNum);
  const novosBlocos = restantes.length ? fatiar(restantes, porDiaNum) : [];
  const instanciaSumiu = !!job.instancia && aparelhos.length > 0 && !aparelhos.includes(job.instancia);

  const salvar = async () => {
    if (!f.instancia)                 { setErro('Escolha o aparelho de envio.'); return; }
    if (f.horarioFim <= f.horario)    { setErro('O horário final precisa ser depois do inicial.'); return; }
    if (!f.inicio)                    { setErro('Informe a data de início.'); return; }
    setSalvando(true); setErro('');
    try {
      const patch = {
        titulo:         f.titulo.trim() || job.titulo || 'Disparo',
        missao:         f.missao,
        segmento:       f.segmento,
        instancia:      f.instancia,
        inicio:         f.inicio,
        horario:        f.horario,
        horarioFim:     f.horarioFim,
        fimDeSemana:    !!f.fimDeSemana,
        disparosPorDia: porDiaNum,
        editadoEm:      new Date().toISOString(),
      };
      // Só reescreve os blocos quando o volume por dia mudou de fato.
      if (mudouPorDia && restantes.length) {
        const todos = [...travados, ...novosBlocos];
        patch.blocosJson  = JSON.stringify(todos);
        patch.totalBlocos = todos.length;
      }
      await updateDoc(doc(db, 'users', uid, 'agendamentos', job.id), patch);
      onSalvo(`✅ Disparo "${patch.titulo}" atualizado. Aparelho: ${patch.instancia} · ${patch.disparosPorDia}/dia · a partir de ${fmtData(patch.inicio)} às ${patch.horario}.`);
    } catch (e) {
      console.error('editar-agendamento', e);
      setErro('Não consegui salvar: ' + e.message);
      setSalvando(false);
    }
  };

  return (
    <div style={modalBg} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
          <div>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '18px' }}>Editar disparo</h2>
            <div style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace', marginTop: '4px' }}>
              {job.totalEnviado || 0} de {job.totalContatos || 0} já enviados · {restantes.length} ainda na fila
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '22px', cursor: 'pointer', color: '#9898b8' }}>×</button>
        </div>

        {instanciaSumiu && (
          <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#dc2626', lineHeight: 1.5 }}>
            ⚠ O aparelho <b>{job.instancia}</b> não aparece mais na sua lista de números conectados.
            Enquanto não trocar, nenhuma mensagem sai. Escolha abaixo um número conectado.
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={lbl}>Aparelho de envio</label>
          {aparelhos.length === 0 ? (
            <div style={{ ...inp, display: 'flex', alignItems: 'center', color: '#9898b8', fontSize: '13px' }}>
              📵 Nenhum número conectado — conecte um em Configurações
            </div>
          ) : (
            <select value={f.instancia} onChange={e => set('instancia', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              {!aparelhos.includes(f.instancia) && f.instancia && (
                <option value={f.instancia}>{f.instancia} (desconectado)</option>
              )}
              {aparelhos.map((nome, i) => <option key={i} value={nome}>{nome}</option>)}
            </select>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div><label style={lbl}>Recomeçar em</label>
            <input type="date" value={f.inicio} onChange={e => set('inicio', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Das</label>
            <input type="time" value={f.horario} onChange={e => set('horario', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Até</label>
            <input type="time" value={f.horarioFim} onChange={e => set('horarioFim', e.target.value)} style={inp} /></div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '14px', alignItems: 'start', marginBottom: '16px' }}>
          <div>
            <label style={lbl}>Quantos por dia</label>
            <input type="number" min="1" max="300" value={f.porDia} onChange={e => set('porDia', e.target.value)} style={inp} />
          </div>
          <div style={{ fontSize: '12px', color: '#52526e', lineHeight: 1.5, paddingTop: '22px' }}>
            {restantes.length === 0
              ? 'Não há contatos na fila — mudar o volume não muda mais nada aqui.'
              : mudouPorDia
                ? <>Os <b>{restantes.length}</b> que ainda não saíram passam a <b>{novosBlocos.length}</b> dia{novosBlocos.length === 1 ? '' : 's'} de envio. Quem já recebeu não recebe de novo.</>
                : <>Hoje: <b>{restantes.length}</b> na fila, em {Math.max(1, (job.totalBlocos || 1) - primeiroLivre)} dia(s).</>}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#52526e', marginBottom: '16px', cursor: 'pointer', width: 'fit-content' }}>
          <input type="checkbox" checked={f.fimDeSemana} onChange={e => set('fimDeSemana', e.target.checked)} />
          Enviar também aos fins de semana
        </label>

        <div style={{ marginBottom: '14px' }}>
          <label style={lbl}>Nome do disparo</label>
          <input value={f.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Ex: Reativação agosto" style={inp} />
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={lbl}>Segmento <span style={{ opacity: .5 }}>(opcional)</span></label>
          <input value={f.segmento} onChange={e => set('segmento', e.target.value)} placeholder="Ex: Barbearia" style={inp} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={lbl}>Missão / mensagem base</label>
          <textarea value={f.missao} onChange={e => set('missao', e.target.value)}
            style={{ ...inp, minHeight: '90px', resize: 'vertical' }} />
          <div style={{ fontSize: '11px', color: '#9898b8', marginTop: '6px' }}>
            Vale só para as mensagens que ainda não saíram.
          </div>
        </div>

        {erro && (
          <div style={{ padding: '10px 14px', borderRadius: '9px', marginBottom: '14px', fontSize: '12px',
            fontFamily: 'monospace', background: 'rgba(239,68,68,0.08)', color: '#dc2626',
            border: '1px solid rgba(239,68,68,0.2)' }}>⚠ {erro}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onClose} style={btnGhostModal}>Cancelar</button>
          <button onClick={salvar} disabled={salvando} style={{
            background: salvando ? '#9898b8' : 'linear-gradient(135deg, #6c47ff, #5535e0)',
            color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 24px',
            fontSize: '14px', fontWeight: '600', cursor: salvando ? 'not-allowed' : 'pointer' }}>
            {salvando ? '⟳ Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

function JobCard({ uid, job, aparelhos = [], onReutilizar, onSoGrupo, onEditar, onPausar, onRetomar, onCancelar }) {
  const [aberto, setAberto] = useState(false);
  const [envios, setEnvios] = useState(null);
  const st = job.status || 'aguardando';
  const ui = STATUS_UI[st] || STATUS_UI.aguardando;
  const total = job.totalContatos || 0, enviado = job.totalEnviado || 0;
  const pct = total ? Math.round(enviado / total * 100) : 0;
  const totalBl = job.totalBlocos || 0, blAtual = job.blocoAtual || 0;
  // aparelho que sumiu da lista = disparo parado sem avisar ninguém
  const instanciaSumiu = !!job.instancia && aparelhos.length > 0 && !aparelhos.includes(job.instancia)
    && ['aguardando', 'ativo', 'pausado'].includes(st);

  useEffect(() => {
    if (!aberto || !uid) return;
    const q = query(collection(db, 'users', uid, 'agendamentos', job.id, 'envios'), orderBy('enviadoEm', 'desc'), limit(500));
    const unsub = onSnapshot(q, snap => setEnvios(snap.docs.map(d => d.data())), () => setEnvios([]));
    return () => unsub();
  }, [aberto, uid, job.id]);

  return (
    <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '14px 16px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.titulo || 'Sem título'}</div>
          <div style={{ fontSize: '11px', color: '#9898b8', fontFamily: 'monospace', marginTop: '3px' }}>
            {job.inicioImediato ? `iniciado ${fmtDataHora(job.criadoEm?.toDate ? job.criadoEm.toDate().toISOString() : job.criadoEm)}` : `a partir de ${fmtData(job.inicio)} · ${job.horario}–${job.horarioFim || HORA_FIM_PADRAO}`}
            {' · '}{job.disparosPorDia}/dia · {job.instancia}{job.fimDeSemana ? ' · inclui fim de semana' : ''}
          </div>
        </div>
        <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', fontFamily: 'monospace', background: ui.bg, color: ui.cor, whiteSpace: 'nowrap' }}>{ui.txt}</span>
      </div>
      {totalBl > 0 && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
          {Array.from({ length: Math.min(totalBl, 60) }, (_, k) => (
            <div key={k} style={{ height: '6px', flex: 1, borderRadius: '3px',
              background: st === 'cancelado' ? '#fca5a5' : k < blAtual ? '#6c47ff' : (k === blAtual && st === 'ativo') ? '#a78bfa' : '#e5e7eb' }} />
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9898b8', fontFamily: 'monospace' }}>
        <span>{enviado}/{total} enviados · {pct}%{job.totalErros > 0 && <span style={{ color: '#dc2626' }}> · {job.totalErros} falhas</span>}</span>
        <span>Dia {Math.min(blAtual + 1, totalBl || 1)}/{totalBl || 1}</span>
      </div>
      {st === 'ativo' && job.ultimoErro && (
        <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '6px', fontFamily: 'monospace' }}>último erro: {job.ultimoErro}</div>
      )}
      {instanciaSumiu && (
        <div style={{ marginTop: '8px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.22)',
          borderRadius: '9px', padding: '9px 12px', fontSize: '11.5px', color: '#dc2626', lineHeight: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <span>⚠ O aparelho <b>{job.instancia}</b> não está mais conectado — nada será enviado até trocar.</span>
          {onEditar && <button onClick={onEditar} style={btnMini('#dc2626')}>Trocar aparelho</button>}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => setAberto(a => !a)} style={btnMini('#52526e')}>{aberto ? '▲ Ocultar envios' : '▼ Ver envios'}</button>
          {onEditar && <button onClick={onEditar} style={btnMini('#6c47ff')}>✎ Editar</button>}
          <button onClick={onReutilizar} style={btnMini('#6c47ff')}>↻ Repetir</button>
          <button onClick={onSoGrupo} style={btnMini('#6c47ff')}>👥 Usar só o grupo</button>
        </div>
        {(onPausar || onRetomar || onCancelar) && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {st === 'pausado'
              ? <button onClick={onRetomar} style={btnMini('#059669')}>▶ Retomar</button>
              : <button onClick={onPausar} style={btnMini('#d97706')}>⏸ Pausar</button>}
            <button onClick={onCancelar} style={btnMini('#dc2626')}>✕ Cancelar</button>
          </div>
        )}
      </div>
      {aberto && (
        <div style={{ marginTop: '10px', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '8px', maxHeight: '260px', overflowY: 'auto' }}>
          {envios === null && <div style={{ fontSize: '12px', color: '#9898b8' }}>Carregando...</div>}
          {envios && envios.length === 0 && <div style={{ fontSize: '12px', color: '#9898b8' }}>Nenhuma mensagem enviada ainda.</div>}
          {envios && envios.map((e, k) => (
            <div key={k} style={{ display: 'grid', gridTemplateColumns: '18px 1fr 1fr auto', gap: '10px', fontSize: '12px', fontFamily: 'monospace', padding: '4px 0', color: e.ok ? '#0a0a18' : '#dc2626' }}>
              <span>{e.ok ? '✅' : '⚠'}</span>
              <span>{e.telefone}</span>
              <span style={{ color: '#52526e', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.nome}{!e.ok && e.erro ? ` · ${e.erro}` : ''}</span>
              <span style={{ color: '#9898b8' }}>{fmtDataHora(e.enviadoEm)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const card = { background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const lbl = { fontSize: '11px', fontWeight: '700', color: '#52526e', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'monospace', display: 'block', marginBottom: '6px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '9px', border: '1.5px solid rgba(0,0,0,0.1)', fontSize: '14px', background: '#f8f8fc', outline: 'none', boxSizing: 'border-box', fontFamily: 'DM Sans, sans-serif', color: '#0a0a18' };
const btnMini = (cor) => ({ background: '#fff', color: cor, border: `1.5px solid ${cor}55`, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' });
const modalBg = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const modalBox = { background: '#fff', borderRadius: '16px', padding: '28px', width: '560px',
  maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' };
const btnGhostModal = { border: '1.5px solid rgba(0,0,0,0.1)', background: 'none', borderRadius: '10px',
  padding: '10px 18px', fontSize: '14px', cursor: 'pointer', color: '#52526e', fontFamily: 'DM Sans, sans-serif' };
